import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ReturnSetup from "./ReturnSetup";
import ReturnReconciliation from "./ReturnReconciliation";
import ReturnCredit from "./ReturnCredit";
import ReturnDisputes from "./ReturnDisputes";
import ReturnLosses from "./ReturnLosses";
import { hasPermission } from "../../utils/permissions";
import "./AccountingPage.css";
const control =
  "mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white px-4";
export default function SupplierReturns() {
  const { accessToken, user } = useAuth();
  const canDispatch =
    hasPermission(user, "accounting.vouchers_post") &&
    hasPermission(user, "purchases.receive");
  const [verified, setVerified] = useState(false),
    [contractVerified, setContractVerified] = useState(false),
    [notice, setNotice] = useState(""),
    [reload, setReload] = useState(0);
  const [companies, setCompanies] = useState([]),
    [company, setCompany] = useState(""),
    [rows, setRows] = useState([]),
    [id, setId] = useState(""),
    [date, setDate] = useState(""),
    [preview, setPreview] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    apiRequest("/accounting/companies", { accessToken })
      .then((c) => {
        if (active) {
          setCompanies(c);
          setCompany(c[0]?.id || "");
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);
  useEffect(() => {
    let active = true;
    setRows([]);
    setId("");
    setPreview(null);
    setVerified(false);
    setContractVerified(false);
    if (company)
      apiRequest(`/accounting/${company}/supplier-returns`, { accessToken })
        .then((r) => {
          if (active) setRows(r);
        })
        .catch((e) => {
          if (active) setError(e.message);
        });
    return () => {
      active = false;
    };
  }, [company, accessToken, reload]);
  async function load(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    setPreview(null);
    setVerified(false);
    setContractVerified(false);
    try {
      setPreview(
        await apiRequest(
          `/accounting/${company}/supplier-returns/${id}/preview?date=${date}`,
          { accessToken },
        ),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function dispatch() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await apiRequest(
        `/accounting/${company}/supplier-returns/${id}/dispatch`,
        {
          method: "POST",
          accessToken,
          body: {
            date,
            previewHash: preview.previewHash,
            dispatchVerified: verified,
            ...(preview.sorAgreementReview ? {contractTermsVerified:contractVerified} : {}),
          },
        },
      );
      setNotice(
        `Dispatched with ${result.voucher.number}. Supplier dues and GST remain unchanged; supplier credit acceptance is pending.`,
      );
      setPreview(null);
      setVerified(false);
      setContractVerified(false);
      setReload((n) => n + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="accounting-master space-y-6">
      <header>
        <p className="text-sm text-gray-500">Accounting / Supplier Returns</p>
        <h1 className="mt-1 text-3xl font-bold text-gray-900">
          Supplier Returns
        </h1>
        <p className="mt-2 text-gray-500">
          Review return quantities and accounting values against the original
          purchase invoice.
        </p>
      </header>
      <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Preview changes no balances. Reviewed dispatch atomically reduces stock
        and posts debit return clearing / credit inventory. Supplier dues and
        GST remain unchanged until separate verified supplier-credit acceptance.
        Full or proportional partial goods and original GST credits are enabled; other
        credits need reconciliation. Original lot, unreserved stock and matching
        valuation are required. Legacy dispatch stays blocked for accounted
        returns.
      </p>
      {error && (
        <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-xl bg-green-50 p-4 text-green-700">
          {notice}
        </p>
      )}
      <ReturnSetup
        company={company}
        onSaved={() => {
          setPreview(null);
          setReload((n) => n + 1);
        }}
      />
      <ReturnReconciliation company={company} reload={reload} />
      <form
        onSubmit={load}
        className="space-y-5 rounded-3xl border border-gray-100 bg-white p-6"
      >
        <fieldset disabled={busy} className="grid gap-5 md:grid-cols-2">
          <label>
            Accounting company
            <select
              required
              className={control}
              value={company}
              onChange={(e) => {
                setPreview(null);
                setError("");
                setCompany(e.target.value);
              }}
            >
              <option value="">Select company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Supplier return
            <select
              required
              className={control}
              value={id}
              onChange={(e) => {
                setPreview(null);
                setId(e.target.value);
              }}
            >
              <option value="">Select return</option>
              {rows.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.returnNumber} · {r.supplier.displayName} · {r.status}
                </option>
              ))}
            </select>
          </label>
          <label>
            Proposed accounting date
            <input
              required
              type="date"
              className={control}
              value={date}
              onChange={(e) => {
                setPreview(null);
                setDate(e.target.value);
              }}
            />
          </label>
        </fieldset>
        <button
          disabled={busy || !id}
          className="h-11 rounded-xl bg-primary-700 px-5 font-medium text-white disabled:opacity-50"
        >
          Preview Return
        </button>
        <p className="text-xs text-gray-500">
          Latest 200 returns linked to source-accounted invoices. Create the
          draft through Purchases → Supplier Returns first.
        </p>
      </form>
      {preview && (
        <section className="space-y-5 rounded-3xl border border-gray-100 bg-white p-6">
          <h2 className="text-xl font-semibold">
            {preview.returnNumber} · Invoice {preview.originalInvoice}
          </h2>
          <p className="text-sm text-gray-600">{preview.explanation}</p>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>Original line</th>
                  <th>Return quantity</th>
                  <th>Previously returned</th>
                  <th>Goods</th>
                  <th>CGST</th>
                  <th>SGST</th>
                  <th>IGST</th>
                  <th>Proposed credit</th>
                </tr>
              </thead>
              <tbody>
                {preview.details.map((i) => (
                  <tr key={i.returnItemId}>
                    <td>{i.originalReceiptItemId}</td>
                    <td>{i.quantity}</td>
                    <td>{i.previousReturnedQuantity}</td>
                    <td>{i.goods}</td>
                    <td>{i.cgst}</td>
                    <td>{i.sgst}</td>
                    <td>{i.igst}</td>
                    <td>{i.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="font-semibold">
            Proposed goods and GST credit: ₹{preview.totals.total}
          </p>
          <p className="text-sm text-gray-500">
            Freight refund: ₹{preview.freightRefund}. Original invoice rounding
            refund: ₹{preview.originalRoundOffRefund}. Neither is automatically
            reversed. Actual supplier credit and GST treatment require
            verification.
          </p>
          {preview.warnings.map((w, i) => (
            <p key={i} className="text-sm text-amber-800">
              {w}
            </p>
          ))}
          <div className="rounded-xl bg-gray-50 p-4 text-sm">
            <p className="font-semibold">
              {preview.blockers.length ? 'Review before proceeding' : 'Source review complete — use the separate verified action below'}
            </p>
            {preview.blockers.map((b) => (
              <p key={b}>{b.replaceAll("_", " ")}</p>
            ))}
          </div>
        </section>
      )}
      {preview && canDispatch && preview.status === "DRAFT" && (
        <section className="space-y-4 rounded-3xl border border-gray-100 bg-white p-6">
          <h2 className="text-xl font-semibold">
            Dispatch with clearing entry
          </h2>
          <p className="text-sm text-gray-600">
            Goods value only: ₹{preview.totals.goods}. This is not a supplier
            credit note and will not reduce outstanding dues.
          </p>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              disabled={busy || !preview.dispatchEnabled}
              checked={verified}
              onChange={(e) => setVerified(e.target.checked)}
            />
            I confirm physical dispatch, original lot/quantities and the
            inventory-to-clearing value. I understand supplier credit acceptance
            is still pending.
          </label>
          {preview.sorAgreementReview && <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm"><p className="font-semibold">Saved SOR return agreement</p><p>Last permitted dispatch: {preview.sorAgreementReview.lastDispatchDate}. Dispatch must also be within the window today.</p>{preview.sorAgreementReview.contracts.map(c=><p key={c.agreementId}>{c.brandName || 'Supplier'}: {c.returnConditions}</p>)}<label className="mt-3 flex items-start gap-3"><input type="checkbox" disabled={busy || !preview.dispatchEnabled} checked={contractVerified} onChange={e=>setContractVerified(e.target.checked)}/>I verified the saved return window and that these goods meet the supplier's return conditions.</label></div>}
          <button
            type="button"
            disabled={busy || !verified || !preview.dispatchEnabled || (preview.sorAgreementReview && !contractVerified)}
            onClick={dispatch}
            className="h-11 rounded-xl bg-primary-700 px-5 font-medium text-white disabled:opacity-50"
          >
            Dispatch &amp; Post Clearing
          </button>
        </section>
      )}
      {preview?.status === "DISPATCHED" && (
        <ReturnLosses company={company} returnId={id} onPosted={(r)=>{setNotice(`Independent loss approval posted${r.voucher?.number?' as '+r.voucher.number:''}. Stock, supplier dues and cash unchanged.`);setPreview(null);setReload(n=>n+1);}}/>
      )}
      {preview?.status === "DISPATCHED" && !preview.loss && <ReturnDisputes company={company} returnId={id} onChanged={()=>{setNotice('Supplier decision recorded. No financial balances changed.');setPreview(null);setReload(n=>n+1);}}/>}
      {preview?.status === "DISPATCHED" && !preview.loss && (
        <ReturnCredit
          company={company}
          returnId={id}
          postingDate={date}
          totals={preview.totals}
          acceptedCredit={preview.acceptedCredit}
          creditHistory={preview.creditHistory}
          remainingCredit={preview.remainingCredit}
          creditHold={preview.creditHold}
          onAccepted={(result) => {
            setNotice(`Supplier credit accepted with ${result.voucher.number}. The credit is unapplied until explicitly allocated in Party Accounts; no cash refund was recorded.`);
            setPreview(null);
            setReload((n) => n + 1);
          }}
        />
      )}
    </main>
  );
}
